import React, {useMemo, useState} from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import {toast} from 'react-toastify'
import fetcher from '../../../../fetcher'
import {
    ACCEPTED_EXTENSIONS,
    ACCEPTED_LABEL,
    autoMapColumns,
    buildCanonicalImportRows,
    detectHeaderRow,
    getDataRows,
    getHeaders,
    getMissingRequiredMappings,
    hasAcceptedExtension,
    parseFile,
} from './csvImport'

const STEPS = [
    {key: 'upload', label: '1. Upload'},
    {key: 'map', label: '2. Map columns'},
    {key: 'preview', label: '3. Preview & validate'},
    {key: 'done', label: '4. Done'},
]

// Core fields lead the preview table; every other mapped field follows. The
// preview scrolls horizontally, so a wide file shows all of its columns.
const CORE_KEYS = ['name', 'phone', 'email']

// Row statuses the import API returns, and how each reads to a person.
const STATUS_LABELS = {
    READY: 'Ready',
    WARNING: 'Warning',
    ERROR: 'Error',
    ALREADY_IMPORTED: 'Already imported',
    IMPORTED: 'Imported',
    FAILED: 'Failed',
}

const STATUS_TONE = {
    READY: 'ready',
    IMPORTED: 'ready',
    WARNING: 'attention',
    ALREADY_IMPORTED: 'info',
    ERROR: 'error',
    FAILED: 'error',
}

/** The row's warnings and errors as one short sentence. */
const rowDetail = (row) => {
    const messages = [...(row.errors || []), ...(row.warnings || [])]
    if (messages.length) return messages.join(' · ')
    if (row.status === 'ALREADY_IMPORTED') return 'Imported previously — it will be skipped.'
    return ''
}

/** Server message if there is one, else our own copy — the `e?.response?.data`
 *  idiom used across the CRM stores. `preview_token` is checked first because a
 *  rejected token carries the one message the user must act on. */
const apiMessage = (error, fallback) =>
    error?.response?.data?.preview_token || error?.response?.data?.detail || fallback

const Stepper = ({current}) => {
    const currentIndex = STEPS.findIndex((s) => s.key === current)

    return (
        <ol className="di-steps">
            {STEPS.map((step, index) => {
                const state = index === currentIndex ? 'current' : index < currentIndex ? 'complete' : 'future'
                return (
                    <li
                        key={step.key}
                        className={`di-step is-${state}`}
                        aria-current={state === 'current' ? 'step' : undefined}
                    >
                        {step.label}
                    </li>
                )
            })}
        </ol>
    )
}

/**
 * Upload → Map columns → Preview & validate → Done.
 *
 * Preview and Done are driven entirely by the import API: statuses, counts and
 * per-row messages are what the server said, never re-derived here. The
 * preview token binds a commit to the exact payload that was previewed, so any
 * change to the file, the mapping, the header row or the import type discards
 * it and the user has to preview again.
 *
 * Only /crm/imports/* is called. The legacy bulk_upload endpoints write into
 * the old CRM and are never reachable from here.
 */
export const CsvImportWizard = ({config}) => {
    const [step, setStep] = useState('upload')
    const [fileName, setFileName] = useState('')
    const [rows, setRows] = useState([])
    const [parseErrors, setParseErrors] = useState([])
    const [sheetList, setSheetList] = useState([])
    const [fileError, setFileError] = useState('')
    const [headerRowIndex, setHeaderRowIndex] = useState(0)
    const [showHeaderPicker, setShowHeaderPicker] = useState(false)
    const [mapping, setMapping] = useState({})
    const [isDragging, setIsDragging] = useState(false)

    // Everything the API told us. Cleared together, because a token is only
    // valid for the payload it was issued against.
    const [preview, setPreview] = useState(null)
    const [previewToken, setPreviewToken] = useState('')
    const [previewError, setPreviewError] = useState('')
    const [isPreviewing, setIsPreviewing] = useState(false)
    const [commitResult, setCommitResult] = useState(null)
    const [isCommitting, setIsCommitting] = useState(false)

    const template = config.fields
    const headers = useMemo(() => getHeaders(rows, headerRowIndex), [rows, headerRowIndex])
    const dataRowCount = useMemo(() => getDataRows(rows, headerRowIndex).length, [rows, headerRowIndex])
    const sampleRow = useMemo(() => getDataRows(rows, headerRowIndex)[0] || [], [rows, headerRowIndex])
    const missingRequired = useMemo(() => getMissingRequiredMappings(template, mapping), [template, mapping])
    // The exact payload sent to preview, reused verbatim at commit so the two
    // can never disagree about what was approved.
    const canonicalRows = useMemo(
        () => buildCanonicalImportRows({rows, headerRowIndex, mapping, template}),
        [rows, headerRowIndex, mapping, template]
    )

    const previewRows = preview?.rows || []
    const summary = preview?.summary || null

    const previewKeys = useMemo(() => {
        const mapped = []
        Object.keys(mapping)
            .sort((a, b) => Number(a) - Number(b))
            .forEach((sourceIndex) => {
                const key = mapping[sourceIndex]
                if (key && !mapped.includes(key)) mapped.push(key)
            })
        const core = CORE_KEYS.filter((key) => mapped.includes(key))
        const custom = mapped.filter((key) => !CORE_KEYS.includes(key))
        return [...core, ...custom]
    }, [mapping])

    const labelForKey = (key) => template.find((field) => field.key === key)?.name || key

    /** Drop everything the API told us. Any change to what would be sent makes
     *  the token invalid, so it is discarded rather than left to fail later. */
    const invalidatePreview = () => {
        setPreview(null)
        setPreviewToken('')
        setPreviewError('')
        setCommitResult(null)
    }

    const reset = () => {
        setStep('upload')
        setFileName('')
        setRows([])
        setParseErrors([])
        setSheetList([])
        setFileError('')
        setHeaderRowIndex(0)
        setShowHeaderPicker(false)
        setMapping({})
        invalidatePreview()
    }

    const applyHeaderRow = (index) => {
        setHeaderRowIndex(index)
        // A different header row means different source columns, so the old
        // mapping no longer points at the same data.
        setMapping(autoMapColumns(getHeaders(rows, index), template))
        invalidatePreview()
    }

    const changeMapping = (sourceIndex, key) => {
        setMapping((prev) => ({...prev, [sourceIndex]: key}))
        invalidatePreview()
    }

    /** Ask the server to plan the import. Triggered by Continue, never by an
     *  effect, so it cannot fire twice on a re-render. */
    const runPreview = async () => {
        if (isPreviewing) return
        setPreviewError('')
        setIsPreviewing(true)
        setPreview(null)
        setPreviewToken('')
        setStep('preview')

        try {
            const res = await fetcher.previewCrmImport({type: config.type, rows: canonicalRows})
            setPreview(res.data)
            setPreviewToken(res.data.preview_token)
        } catch (error) {
            // Stay on Preview with the failure visible: the file and mapping are
            // untouched, so the user can go Back or retry.
            const message = apiMessage(error, "We couldn't check this file. Please try again.")
            setPreviewError(message)
            toast.error(message)
        } finally {
            setIsPreviewing(false)
        }
    }

    const runCommit = async () => {
        if (isCommitting || !previewToken) return
        setIsCommitting(true)

        try {
            const res = await fetcher.commitCrmImport({
                type: config.type,
                rows: canonicalRows,
                preview_token: previewToken,
            })
            setCommitResult(res.data)
            setStep('done')
        } catch (error) {
            const message = apiMessage(error, `We couldn't import these ${config.label.toLowerCase()}. Please try again.`)
            // A rejected token means the payload no longer matches what was
            // approved; forcing a fresh preview is the only correct recovery.
            if (error?.response?.status === 400) {
                setPreviewToken('')
                setPreview(null)
            }
            setPreviewError(message)
            toast.error(message)
        } finally {
            setIsCommitting(false)
        }
    }

    const handleFile = async (file) => {
        if (!file) return
        setFileError('')
        setParseErrors([])
        setSheetList([])
        invalidatePreview()

        if (!hasAcceptedExtension(file.name)) {
            setFileError(`That file type isn't supported. Upload a ${ACCEPTED_LABEL} file.`)
            return
        }

        let parsed
        try {
            parsed = await parseFile(file)
        } catch (e) {
            setFileError("We couldn't read that file. Please check it opens, then try again.")
            return
        }

        const {rows: parsedRows, errors, sheetList: sheets} = parsed

        if (!parsedRows.length) {
            setFileError('That file is empty.')
            return
        }

        const detected = detectHeaderRow(parsedRows)
        if (!getDataRows(parsedRows, detected).length) {
            setFileError("That file doesn't have any data rows below its header.")
            return
        }

        setFileName(file.name)
        setRows(parsedRows)
        setParseErrors(errors)
        setSheetList(sheets || [])
        setHeaderRowIndex(detected)
        setShowHeaderPicker(false)
        setMapping(autoMapColumns(getHeaders(parsedRows, detected), template))
    }

    const onDrop = (event) => {
        event.preventDefault()
        setIsDragging(false)
        handleFile(event.dataTransfer?.files?.[0])
    }

    const renderUpload = () => (
        <div className="di-body">
            <label
                className={`di-drop${isDragging ? ' is-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onDrop}
            >
                <input
                    type="file"
                    accept={ACCEPTED_EXTENSIONS.join(',')}
                    hidden
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <span className="di-drop-title">Drop your file here, or click to browse</span>
                <span className="di-drop-hint">{ACCEPTED_LABEL} files</span>
            </label>

            {fileError && <p className="di-error" role="alert">{fileError}</p>}

            {Boolean(fileName) && !fileError && (
                <div className="di-file">
                    <div className="di-file-name">{fileName}</div>
                    <div className="di-file-meta">
                        {dataRowCount} {dataRowCount === 1 ? 'row' : 'rows'} · using row {headerRowIndex + 1} as the header
                        {' '}
                        <button type="button" className="di-link" onClick={() => setShowHeaderPicker((v) => !v)}>
                            Use a different row
                        </button>
                    </div>

                    {showHeaderPicker && (
                        <div className="di-header-picker">
                            <label htmlFor="di-header-row">Header row</label>
                            <select
                                id="di-header-row"
                                value={headerRowIndex}
                                onChange={(e) => applyHeaderRow(Number(e.target.value))}
                            >
                                {rows.slice(0, 10).map((row, index) => (
                                    <option key={index} value={index}>
                                        Row {index + 1}: {row.filter(Boolean).join(', ').slice(0, 60) || '(empty)'}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {sheetList.length > 1 && (
                        <p className="di-warning">
                            Only the first sheet ("{sheetList[0]}") will be imported.
                            To import the others, upload each sheet separately.
                        </p>
                    )}

                    {parseErrors.length > 0 && (
                        <p className="di-warning">
                            {parseErrors.length} {parseErrors.length === 1 ? 'row' : 'rows'} had formatting problems
                            and may import incompletely.
                        </p>
                    )}
                </div>
            )}
        </div>
    )

    const renderMap = () => (
        <div className="di-body">
            <p className="di-lede">
                Match the columns in your file to CRM fields. Mapping two columns to the same
                field joins them, so First Name and Last Name can both become Name.
            </p>

            <div className="di-table-wrap">
                <table className="di-table">
                    <thead>
                        <tr><th>Your file column</th><th>Sample value</th><th>Maps to</th></tr>
                    </thead>
                    <tbody>
                        {headers.map((header, index) => (
                            <tr key={index}>
                                <td className="di-col-name">{header}</td>
                                <td className="di-sample">{sampleRow[index] || '—'}</td>
                                <td>
                                    <select
                                        aria-label={`Maps to for ${header}`}
                                        value={mapping[index] || ''}
                                        onChange={(e) => changeMapping(index, e.target.value)}
                                    >
                                        <option value="">Don't import</option>
                                        {template.map((field) => (
                                            <option key={field.key} value={field.key}>
                                                {field.name}{field.required ? ' *' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {missingRequired.length > 0 && (
                <p className="di-error" role="alert">
                    Map a column to {missingRequired.map((field) => field.name).join(', ')} to continue.
                </p>
            )}
        </div>
    )

    const renderSummaryPills = () => {
        if (!summary) return null
        // Only non-zero counts, matching how the rest of Settings reads.
        const pills = [
            ['ready', summary.ready, 'ready'],
            ['warnings', summary.warnings, 'attention'],
            ['errors', summary.errors, 'error'],
            ['already imported', summary.already_imported, 'info'],
        ].filter(([, count]) => count > 0)

        return (
            <div className="di-pills">
                {pills.map(([label, count, tone]) => (
                    <span key={label} className={`di-pill is-${tone}`}>{count} {label}</span>
                ))}
            </div>
        )
    }

    const renderPreview = () => (
        <div className="di-body">
            {isPreviewing && (
                <div className="di-loading" role="status">
                    <CircularProgress size={16} />
                    <span>Checking this file against the CRM…</span>
                </div>
            )}

            {!isPreviewing && previewError && (
                <p className="di-error" role="alert">{previewError}</p>
            )}

            {!isPreviewing && preview && (
                <>
                    {renderSummaryPills()}

                    <p className="di-lede">
                        {summary.errors > 0
                            ? 'Rows with an error are skipped. Everything else will be imported.'
                            : 'This is what will be imported.'}
                    </p>

                    <div className="di-table-wrap di-preview-wrap">
                        <table className="di-table">
                            <thead>
                                <tr>
                                    <th>Row</th>
                                    {previewKeys.map((key) => <th key={key}>{labelForKey(key)}</th>)}
                                    <th>Status</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewRows.map((row) => {
                                    const tone = STATUS_TONE[row.status] || 'info'
                                    const source = canonicalRows[row.row_index] || {}
                                    return (
                                        <tr
                                            key={row.row_index}
                                            className={tone === 'ready' ? undefined : `di-row-${tone}`}
                                        >
                                            <td>{row.row_index + 1}</td>
                                            {previewKeys.map((key) => <td key={key}>{source[key] || '—'}</td>)}
                                            <td>
                                                <span className={`di-pill is-${tone}`}>
                                                    {STATUS_LABELS[row.status] || row.status}
                                                </span>
                                            </td>
                                            <td className="di-detail">{rowDetail(row)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    )

    const renderDone = () => {
        const result = commitResult?.summary || {}
        const imported = result.imported ?? 0
        const noun = imported === 1
            ? config.label.toLowerCase().replace(/s$/, '')
            : config.label.toLowerCase()
        const failedRows = (commitResult?.rows || []).filter((row) => row.status === 'FAILED')

        return (
            <div className="di-body">
                <div className="di-metric">
                    <div className="di-metric-num">{imported}</div>
                    <div className="di-metric-label">{noun} imported</div>
                </div>

                <div className="di-pills di-done-pills">
                    {result.already_imported > 0 && (
                        <span className="di-pill is-info">
                            {result.already_imported} already imported
                        </span>
                    )}
                    {result.failed > 0 && (
                        <span className="di-pill is-error">{result.failed} failed</span>
                    )}
                    {result.warnings > 0 && (
                        <span className="di-pill is-attention">{result.warnings} with warnings</span>
                    )}
                </div>

                {failedRows.length > 0 && (
                    <div className="di-table-wrap di-preview-wrap">
                        <table className="di-table">
                            <thead><tr><th>Row</th><th>Why it failed</th></tr></thead>
                            <tbody>
                                {failedRows.map((row) => (
                                    <tr key={row.row_index} className="di-row-error">
                                        <td>{row.row_index + 1}</td>
                                        <td className="di-detail">
                                            {rowDetail(row) || 'This row could not be imported.'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        )
    }

    const renderFooter = () => {
        if (step === 'upload') {
            return (
                <>
                    <span />
                    <button
                        type="button"
                        className="di-btn di-primary"
                        disabled={!rows.length || Boolean(fileError)}
                        onClick={() => setStep('map')}
                    >
                        Continue
                    </button>
                </>
            )
        }

        if (step === 'map') {
            return (
                <>
                    <button type="button" className="di-btn di-ghost" onClick={() => setStep('upload')}>
                        Back
                    </button>
                    <button
                        type="button"
                        className="di-btn di-primary"
                        disabled={missingRequired.length > 0 || isPreviewing}
                        onClick={runPreview}
                    >
                        Continue
                    </button>
                </>
            )
        }

        if (step === 'preview') {
            // The server decides what is committable; a preview that failed
            // leaves no token, which is what actually blocks Import.
            const canImport = Boolean(previewToken) && !isPreviewing && !isCommitting
            return (
                <>
                    <button
                        type="button"
                        className="di-btn di-ghost"
                        disabled={isCommitting}
                        onClick={() => setStep('map')}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        className="di-btn di-primary"
                        disabled={!canImport}
                        onClick={runCommit}
                    >
                        {isCommitting && (
                            <CircularProgress size={13} sx={{color: '#FFF', marginRight: '7px'}} />
                        )}
                        {isCommitting ? 'Importing…' : `Import ${config.label.toLowerCase()}`}
                    </button>
                </>
            )
        }

        return (
            <>
                <button type="button" className="di-btn di-ghost" onClick={reset}>
                    Import another file
                </button>
                <span />
            </>
        )
    }

    return (
        <div className="di-wizard">
            <div className="di-heading">
                <div className="field-label">{config.title}</div>
                <div className="field-help">{config.description}</div>
            </div>

            <Stepper current={step} />

            {step === 'upload' && renderUpload()}
            {step === 'map' && renderMap()}
            {step === 'preview' && renderPreview()}
            {step === 'done' && renderDone()}

            <div className="di-footer">{renderFooter()}</div>

            {Boolean(fileName) && step !== 'done' && (
                <button type="button" className="di-link di-reset" onClick={reset}>
                    Start over with a different file
                </button>
            )}
        </div>
    )
}
